import type { PlayInfo } from '@module/apis/client-side/live/type';
import type { UserInfo } from '@module/apis/client-side/member/type';
import type { TypedModalService } from '@module/components/Modal/HSMS/types';
import type { CaptionStore } from '@module/features/caption/type';
import type { SponItem } from '@module/stores/useChatMessageStore';
import type { GlobalModal } from '@module/stores/useModalStore';
import type { ChatUIAffectingMessage } from '@module/types/centrifuge/chatTypes';
import type { CentrifugeMessage } from '@module/types/centrifuge/messageTypes';
import type {
  PersonalAdminMessage,
  PersonalManagerOnOffMessage,
  PersonalMissionMessageMessage,
  PersonalOverLapLoginMessage,
  PersonalSupportMessage,
  PersonalUpdateUserInfoMessage,
  PersonalUserAlertMessage,
  PersonalUserStopMessage,
} from '@module/types/centrifuge/personalMessageTypes';
import type {
  FreezeOffMessage,
  FreezeOnMessage,
  MissionMessage,
  VoteMessage,
} from '@module/types/centrifuge/public-message';
import type {
  BlindOffMessage,
  BlindOnMessage,
  CastGuestImgBlindOnMessage,
  CastPauseMessage,
  CastResumeMessage,
  ServiceBlindOffMessage,
  ServiceBlindOnMessage,
} from '@module/types/centrifuge/public-message/blindMessageTypes';
import type { ChatBlockCountryMessage } from '@module/types/centrifuge/public-message/ChatBlockCountryMessageTypes';
import type { CaptionMessage } from '@module/types/centrifuge/public-message/captionMessageTypes';
import type {
  CreateNoticeMessage,
  DeleteNoticeMessage,
  FirstSponMessage,
  IntroEffectMessage,
  MediaUpdateMessage,
  MemberKickOutMessage,
  ModifyRoomMessage,
  RecommendMessage,
  RoomEndMessage,
} from '@module/types/centrifuge/public-message/etcMessageTypes';
import type { EventAnimCoinMessage, EventRankMessage } from '@module/types/centrifuge/public-message/eventMessageTypes';
import type { ExcelBroadcastMessage } from '@module/types/centrifuge/public-message/excelMessageTypes';
import type { HeartIconVIPMessage } from '@module/types/centrifuge/public-message/heartIconVIPMessageTypes';
import type { FanInMessage, KingFanInMessage } from '@module/types/centrifuge/public-message/inMessageTypes';
import type { MuteOffMessage, MuteOnMessage } from '@module/types/centrifuge/public-message/muteMessageTypes';
import type { ServiceCastBlockMessage } from '@module/types/centrifuge/public-message/serviceMessageTypes';
import type { SoundOffMessage, SoundOnMessage } from '@module/types/centrifuge/public-message/soundMessageTypes';
import type { QueryClient } from '@tanstack/react-query';
import type { MediaPlayer } from 'amazon-ivs-player';

/**
 * 메시지 핸들러가 실행될 때 필요한 공통 컨텍스트
 */
export interface MessageHandlerContext {
  // 플레이 정보
  playInfo?: PlayInfo | null;

  // 사용자 정보
  userInfo?: UserInfo | null;
  isLogin?: boolean;
  isAdult?: boolean;
  siteMode?: { mode: string } | null;
  sitePresetCode?: string;

  // Store states
  isGlobalChatVisible?: boolean;

  // Store actions
  setPlayInfo: (playInfo: PlayInfo) => void;
  setChatMessages: (updater: (prev: ChatUIAffectingMessage[]) => ChatUIAffectingMessage[]) => void;
  setMuteTime?: (time: number) => void;
  setMuteOnMessages?: (messages: MuteOnMessage[]) => void;
  setLike?: (count: number) => void;
  setBookmark?: (count: number) => void;
  setVolume?: (volume: number) => void;
  setVolumeBefore?: (volume: number) => void;
  setIsMute?: (isMute: boolean, options?: { persist?: boolean }) => void;
  setMuted?: (muted: boolean, options?: { persist?: boolean }) => void;
  setSupporterNotice?: (notice: string) => void;

  // Player 관련
  dispose: () => void;
  ivsPlayer?: MediaPlayer | null;

  // Chat 관련
  processChatMessage: (payload: ChatUIAffectingMessage) => void;

  // Event 관련
  updateEventRank: (message: { rank: number; score: number; endDateTime: string }) => void;
  enqueueVIPSponEffect?: (payload: HeartIconVIPMessage) => void;

  // Cast 관련
  startRetry?: () => void;

  // Modal 관련 - 플랫폼별로 둘 중 하나만 제공
  // fanza-pc/mobile용 (HSMS 기반)
  // biome-ignore lint/suspicious/noExplicitAny: 플랫폼별 AppModalMap이 다르므로 any 사용
  hsms?: TypedModalService<any>;
  // pandatv-pc용 (zustand store 기반)
  pushModal?: <T>(modal: GlobalModal<T>) => void;
  popModal?: () => void;
  clearModals?: () => void;

  // Router & Query
  router?: {
    push: (path: string) => void;
  };
  queryClient: QueryClient;

  // Refetch
  retryLivePlay?: () => Promise<PlayInfo>;

  // Socket
  disconnectSocket?: () => void;

  // Event triggers (bridge pattern)
  triggers?: {
    eventAnimCoin?: ((payload: EventAnimCoinMessage) => void) | null;
    introEffect?: ((message: IntroEffectMessage['data']['message']) => void) | null;
    castSettingRecommendEffect?: (() => void) | null;
    playCastSettingKingFanInAudio?: (() => void) | null;
    playCastSettingFanInAudio?: (() => void) | null;
    firstSpon?: ((payload: FirstSponMessage) => void) | null;
  };

  // Helpers
  getOutOfRoom?: (message: string) => void;

  // Caption
  addCaption?: CaptionStore['add'];
}

/**
 * 메시지 핸들러 Action 타입
 * - USE_DEFAULT: 핸들러의 기본 동작 실행
 * - null | undefined: 메시지 무시 (skip)
 * - function: 커스텀 동작 실행
 */
export const USE_DEFAULT = 'DEFAULT_ACTION' as const;
export type UseDefaultAction = typeof USE_DEFAULT;

export type MessageHandlerAction<T extends CentrifugeMessage> =
  | ((payload: T, context: MessageHandlerContext) => void)
  | UseDefaultAction
  | null
  | undefined;

/**
 * 메시지 핸들러 인터페이스
 */
export interface MessageHandler {
  /**
   * 메시지가 이 핸들러에 매칭되는지 확인
   */
  canHandle(payload: CentrifugeMessage): boolean;

  /**
   * 메시지 처리
   * @returns void 또는 Promise<void> - 비동기 처리가 필요한 핸들러는 Promise를 반환
   */
  handle(payload: CentrifugeMessage, context: MessageHandlerContext): void | Promise<void>;
}

/**
 * 메시지 디스패처 인터페이스
 */
export interface MessageDispatcher {
  /**
   * 핸들러 등록
   */
  register(handler: MessageHandler): void;

  /**
   * 메시지 처리
   */
  dispatch(payload: CentrifugeMessage, context: MessageHandlerContext): void | Promise<void>;
}

/**
 * 모든 메시지 타입에 대한 Action 정의
 */
export interface MessageHandlerActions {
  // Public Messages
  onChatMessage?: MessageHandlerAction<ChatUIAffectingMessage>;
  onSponMessage?: MessageHandlerAction<SponItem>;

  onCreateNotice?: MessageHandlerAction<CreateNoticeMessage>;
  onDeleteNotice?: MessageHandlerAction<DeleteNoticeMessage>;

  onFreezeOn?: MessageHandlerAction<FreezeOnMessage>;
  onFreezeOff?: MessageHandlerAction<FreezeOffMessage>;

  onMuteOn?: MessageHandlerAction<MuteOnMessage>;
  onMuteOff?: MessageHandlerAction<MuteOffMessage>;

  onMission?: MessageHandlerAction<MissionMessage>;

  onIntroEffect?: MessageHandlerAction<IntroEffectMessage>;
  onEventAnimCoin?: MessageHandlerAction<EventAnimCoinMessage>;
  onEventRank?: MessageHandlerAction<EventRankMessage>;
  onHeartIconVip?: MessageHandlerAction<HeartIconVIPMessage>;

  onVote?: MessageHandlerAction<VoteMessage>;

  onModifyRoom?: MessageHandlerAction<ModifyRoomMessage>;

  onSoundOn?: MessageHandlerAction<SoundOnMessage>;
  onSoundOff?: MessageHandlerAction<SoundOffMessage>;

  onMemberKickOut?: MessageHandlerAction<MemberKickOutMessage> | Promise<MessageHandlerAction<MemberKickOutMessage>>;

  onCastResume?: MessageHandlerAction<CastResumeMessage>;

  onExcelBroadcast?: MessageHandlerAction<ExcelBroadcastMessage>;

  onMediaUpdate?: MessageHandlerAction<MediaUpdateMessage>;

  onBlindOn?: MessageHandlerAction<
    BlindOnMessage | CastGuestImgBlindOnMessage | CastPauseMessage | ServiceBlindOnMessage
  >;
  onBlindOff?: MessageHandlerAction<BlindOffMessage | ServiceBlindOffMessage>;

  onRoomEnd?: MessageHandlerAction<RoomEndMessage> | Promise<MessageHandlerAction<RoomEndMessage>>;

  onRecommend?: MessageHandlerAction<RecommendMessage>;
  onKingFanIn?: MessageHandlerAction<KingFanInMessage>;
  onFanIn?: MessageHandlerAction<FanInMessage>;

  onCaption?: MessageHandlerAction<CaptionMessage>;

  onFirstSpon?: MessageHandlerAction<FirstSponMessage>;

  onServiceCastBlock?: MessageHandlerAction<ServiceCastBlockMessage>;
  onChatBlockCountry?: MessageHandlerAction<ChatBlockCountryMessage>;

  // Personal Messages
  onPersonalAdmin?: MessageHandlerAction<PersonalAdminMessage>;
  onPersonalSupport?: MessageHandlerAction<PersonalSupportMessage>;
  onPersonalUpdateUserInfo?: MessageHandlerAction<PersonalUpdateUserInfoMessage>;
  onPersonalUserAlert?: MessageHandlerAction<PersonalUserAlertMessage>;
  onPersonalUserStop?: MessageHandlerAction<PersonalUserStopMessage>;
  onPersonalOverlapLogin?: MessageHandlerAction<PersonalOverLapLoginMessage>;
  onPersonalMission?: MessageHandlerAction<PersonalMissionMessageMessage>;
  onPersonalManager?: MessageHandlerAction<PersonalManagerOnOffMessage>;
}
